import {
  LOI_STATUS_LABELS,
  categoryPathLabel,
  formatDate,
  formatDateOnly,
  formatDateTime,
} from '@dataroom/shared';
import type { BuyerEngagementDetail, DocumentPageStats } from '@dataroom/shared';
import {
  accessEndedLabel,
  countLabel,
  openedOfVisibleLabel,
  pagesViewedLabel,
  readingTimeLabel,
} from '@/lib/engagement';

interface BuyerEngagementSectionProps {
  engagement: BuyerEngagementDetail | undefined;
  loading: boolean;
  error: string | null;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}

/**
 * ADR-0038 — one buyer's engagement inside the buyer detail: when they were
 * last active, what they opened (views / downloads, when), which category areas
 * those documents sit in, and the NDA / LOI milestones the funnel recorded.
 * Plain facts from the audit log — no score, no reading of intent. Per document,
 * the page-level reading the viewer reported (ADR-0056): "Pages viewed (on
 * screen): 12 of 40 · total time 6 min" and a bar per page showing the time on it.
 */
export function BuyerEngagementSection({
  engagement,
  loading,
  error,
}: BuyerEngagementSectionProps) {
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Activity
      </p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : !engagement ? null : (
        <EngagementBody engagement={engagement} />
      )}
    </div>
  );
}

function EngagementBody({ engagement }: { engagement: BuyerEngagementDetail }) {
  const { summary, milestones, documents, areas } = engagement;
  const loi = milestones.loiSubmittedAt
    ? `${formatDate(milestones.loiSubmittedAt)}${
        milestones.loiStatus ? ` · ${LOI_STATUS_LABELS[milestones.loiStatus]}` : ''
      }${milestones.loiDecidedAt ? ` (${formatDate(milestones.loiDecidedAt)})` : ''}`
    : 'Not submitted';

  const ended = accessEndedLabel(summary);
  return (
    <div className="space-y-4">
      {ended ? (
        <p className="text-sm text-muted-foreground">
          {ended} — what they did while they had access is kept below.
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <Fact label="Last active" value={formatDateTime(summary.lastActiveAt, 'Not yet')} />
        <Fact label="Last signed in" value={formatDateTime(summary.lastSignInAt, 'Not yet')} />
        <Fact label="Documents opened" value={String(summary.documentsOpened)} />
        <Fact label="Of what they can see" value={openedOfVisibleLabel(summary)} />
        <Fact label="Views" value={String(summary.views)} />
        <Fact label="Downloads" value={String(summary.downloads)} />
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/30 p-3 text-sm sm:grid-cols-4">
        <Fact label="NDA signed" value={formatDate(milestones.ndaSignedAt, 'Not yet')} />
        <Fact label="LOI" value={loi} />
        <Fact
          label="Exclusivity expires"
          value={formatDateOnly(milestones.exclusivityExpiresAt, '—')}
        />
        <Fact label="Closing" value={formatDateOnly(milestones.closingDate, '—')} />
      </div>

      {documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          This buyer has not opened any documents yet.
        </p>
      ) : (
        <>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">By area</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-1.5 font-medium">Area</th>
                  <th className="py-1.5 font-medium">Documents</th>
                  <th className="py-1.5 font-medium">Views · downloads</th>
                  <th className="py-1.5 font-medium">Last opened</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => (
                  <tr key={a.areaId ?? 'unfiled'} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 font-medium">{a.areaName ?? 'Not filed yet'}</td>
                    <td className="py-1.5 pr-3">{a.documentsOpened}</td>
                    <td className="py-1.5 pr-3">
                      {countLabel(a.views, 'view')} · {countLabel(a.downloads, 'download')}
                    </td>
                    <td className="py-1.5 pr-3">{formatDateTime(a.lastOpenedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Documents opened</p>
            <ul className="divide-y rounded-md border text-sm">
              {documents.map((d) => (
                <li
                  key={d.documentId}
                  className="flex flex-wrap items-start justify-between gap-2 p-2"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{d.name ?? 'Untitled document'}</p>
                    <p className="text-xs text-muted-foreground">
                      {d.categoryPath.length > 0 ? categoryPathLabel(d.categoryPath, ' › ') : 'Not filed yet'}
                    </p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>
                      {countLabel(d.views, 'view')} · {countLabel(d.downloads, 'download')}
                    </p>
                    <p>Last opened {formatDateTime(d.lastOpenedAt)}</p>
                  </div>
                  {d.pages ? <PageReading name={d.name} stats={d.pages} /> : null}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

/** The page-level facts for one document: pages viewed (on screen) of the page
 * count, total time, and one bar per page (or per run of pages in a long
 * document — the server sends at most 60) whose height is the time on screen. */
function PageReading({ name, stats }: { name: string | null; stats: DocumentPageStats }) {
  const segments = stats.segments;
  const max = Math.max(1, ...segments.map((s) => s.seconds));
  const pageLabel = (first: number, last: number) =>
    first === last ? `Page ${first}` : `Pages ${first}–${last}`;
  return (
    <div className="w-full space-y-1" data-testid="page-reading">
      <p className="text-xs text-muted-foreground">
        {pagesViewedLabel(stats)}
        {stats.versionIsCurrent ? '' : ' (an earlier version)'}
      </p>
      <div
        role="img"
        aria-label={`Time on screen per page of ${name ?? 'the document'}: ${segments
          .filter((s) => s.viewed)
          .map((s) => `${s.firstPage === s.lastPage ? 'page' : 'pages'} ${pageLabel(s.firstPage, s.lastPage).replace(/^Pages? /, '')} ${s.seconds} s`)
          .join(', ')}`}
        className="flex h-8 items-end gap-px rounded border bg-muted/30 p-0.5"
      >
        {segments.map((s) => (
          <div
            key={s.firstPage}
            data-page-bar={s.firstPage}
            title={`${pageLabel(s.firstPage, s.lastPage)}: ${
              s.viewed ? readingTimeLabel(s.seconds) : 'not viewed'
            }`}
            className={`min-w-0 flex-1 rounded-sm ${s.viewed ? 'bg-primary/70' : 'bg-muted'}`}
            style={{ height: s.viewed ? `${Math.max(12, Math.round((s.seconds / max) * 100))}%` : '6%' }}
          />
        ))}
      </div>
    </div>
  );
}
